import { ReactNode, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { DateString } from '../../../../../common/src/date_string.ts';
import {
  currentStreak,
  learnLevelName,
  learnModuleName,
  moduleProgress,
  RecordedSession,
  recordLearnSession,
} from '../../../../../common/src/learn_rules.ts';
import { LearnLevel, LearnModule, LearnProgress } from '../../../../../common/src/model.ts';
import {
  alignTap,
  generateSession,
  PatternResult,
  RandomSource,
  rhythmBeatsPerBar,
  RhythmInput,
  RhythmTimeline,
  summarizeSession,
  WarmUpTimeline,
} from '../../../../../common/src/rhythm_echo.ts';
import { LearnProgressUpdate, useLearnProgress } from '../../../data/use_learn_progress.tsx';
import { OuterPageContent } from '../../../util/shared.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { Header } from '../../header.tsx';
import { CaptureSlice, sliceCapture } from '../audio/capture_slice.ts';
import { levelForDbfs } from '../audio/frame_reading.ts';
import { getLearnAudioSession, LearnAudioSession } from '../audio/learn_audio_session.ts';
import { MicFailure, micFailureOf, MicPermissionState, queryMicPermission } from '../audio/learn_mic_capture.ts';
import { useAudioClock, useEnterKey, useSpaceBarTap } from '../audio/use_audio_clock.ts';
import { learnModuleBack } from '../learn_classes.ts';
import { nowOn } from '../learn_dates.ts';
import { SingingFailureScreen } from '../learn_failure_screen.tsx';
import { HearingYouMeter } from '../learn_meter.tsx';
import { useLearnExit } from '../learn_navigation.tsx';
import { RangePermissionScreen } from '../range/range_permission_screen.tsx';
import {
  calibrationBar,
  calibrationBarClicksMs,
  calibrationCue,
  calibrationLights,
  calibrationOutcome,
  calibrationPhase,
  CalibrationRun,
  calibrationStreak,
  calibrationTap,
  calibrationTick,
  isCalibrating,
  isCalibrationTapOpen,
  isWarmUpTapOpen,
  startCalibrationRun,
} from './rhythm_calibration_run.ts';
import { RhythmCalibrationScreen } from './rhythm_calibration_screen.tsx';
import {
  initialRhythmEchoState,
  isTapOpen,
  reduceRhythmEcho,
  RhythmEchoState,
  RhythmPatternState,
  RhythmReplay,
} from './rhythm_echo_session.ts';
import { RhythmPatternScreen } from './rhythm_pattern_screen.tsx';
import { RhythmResultScreen } from './rhythm_result_screen.tsx';
import { RhythmStartScreen } from './rhythm_start_screen.tsx';
import { RhythmSummaryScreen } from './rhythm_summary_screen.tsx';
import { giveTapFeedback } from './rhythm_tap_feedback.ts';

// Rhythm Echo (issue #3473): the page behind the Rhythm card on the Learn hub. Three screens in
// turn - the start screen, the warm-up bar when the device has no tap offset yet (or from the
// Recalibrate link), and the session itself: eight patterns, each one silent lead beat, the
// pattern (with the silent Ready · Set pulsing over its last two beats) and then straight away the
// singer's taps from Go!, then its result (with one
// Try again per pattern, whose result replaces the first attempt), and the summary after the
// eighth, which is the only point that records progress. No footer; the header's back button
// returns to the Rhythm class screen (issue #3719) and leaving mid-session records nothing.
//
// Feedback on tap. Every tap the exercise keeps (a session tap inside the tapping window, a warm-up
// tap once the clicks are the singer's to tap with) gives a haptic tick where the device has one
// and, where the audio output is fast enough to sound with the tap, the same percussive hit the
// pattern was played with (giveTapFeedback, issue #3710). Ignored taps give nothing.
//
// Time. Every phase change comes from the audio clock: while a pattern, a replay or a warm-up is
// running, useAudioClock polls audio.nowMs() on requestAnimationFrame and the reading goes to
// the pure reducer (rhythm_echo_session.ts, rhythm_calibration_run.ts) as a tick. Nothing here
// uses Date.now or setTimeout for a transition, so a story that injects the fake session and moves
// its clock drives the page through exactly the transitions a real clock would. Sounds are
// scheduled from the timeline the reducer built, in an effect that fires once per new timeline
// (the warm-up's clicks a bar at a time, see RhythmCalibration), so what plays and what the state
// machine expects are the same numbers.
//
// Injection. `audio` defaults to the page-wide Learn audio session and `random` to Math.random;
// stories and tests pass the fake session and a seeded source. `initialScreen` lets a story open
// on the warm-up or the session without the Start tap (the real page always starts on the start
// screen, because that tap is the user gesture that starts audio).
//
// Sing Rhythm (issue #3723) is this page with a variant of its own: the same patterns, timeline,
// scoring, result and summary, with the rhythm sung back on one note instead of tapped. What
// differs is listed here once, and each piece below says where it applies:
//
//   - The microphone. Start (and the Calibrate link) open it, behind the Range Finder's own
//     explanation screen unless the browser has already granted it, and a microphone that will not
//     open gets the shared failure screen, whose Try again is another gesture to open it with. It
//     stays open through the session and is ended at the summary, on the way back to the start
//     screen from a warm-up, and by leave().
//   - The input. The session's audio thread times the start of every sung note (onSungOnset); each
//     reaches the reducer as a tap at that time less the voice offset. Pitch plays no part.
//   - The warm-up. The singer sings "ta" with the clicks, and what locks is the voice offset: the
//     speaker's delay plus the microphone's, kept apart from the tap offset. A sung note can read
//     most of half a second late on an Android phone, more than the half beat the warm-up matches
//     a click within, so the reported output delay is taken off each note before it is matched and
//     added back to the offset that is stored.
//   - Hear both again. The pattern is played as percussion, then the singer's own take: the sung
//     bar cut from the session's rolling capture when the pattern is scored and kept in memory
//     until the next one. Nothing is stored or uploaded.

export const rhythmEchoPageId = 'rhythm-echo-page';

// What makes one rhythm exercise differ from the other: the module its sessions are recorded
// under, how the rhythm is given back, and the page's own id
export interface RhythmExerciseVariant {
  module: LearnModule;
  input: RhythmInput;
  pageId: string;
  idPrefix: string; // for the shared failure screen's ids
}

const rhythmEchoVariant: RhythmExerciseVariant = {
  module: LearnModule.RhythmEcho,
  input: 'tap',
  pageId: rhythmEchoPageId,
  idPrefix: 'rhythm',
};

// start: the start screen. permission: the microphone explained, before it is asked for (voice
// only). failed: the microphone would not open (voice only). calibration: the warm-up. session:
// the eight patterns and their summary.
export type RhythmEchoScreen = 'start' | 'permission' | 'failed' | 'calibration' | 'session';

// Where a way in is headed: Start leads to the session (through the warm-up on a device with no
// offset yet), the Calibrate link to the warm-up and back to the start screen
type RhythmEntry = 'session' | 'start';

// The levels this build can play; Medium and Hard wait for their vocabulary slice
const selectableLevels: LearnLevel[] = [LearnLevel.Easy];

// A pattern's startMs is simply the audio clock at the button tap: the one-beat lead before the
// pattern sounds lives in the timeline (issue #3519). "Hear both again" has no timeline of its
// own, so it keeps a small lead of its own so the first onset is never scheduled in the past.
export const replayLeadMs = 300;

// How long a sung pattern waits, past the voice offset, before it is scored: the detector's own
// confirmation of a note (50 ms) and the rolling capture's last chunk of the bar (171 ms at 48 kHz)
// both have to have arrived
export const voiceSettleMs = 300;

// How far before Go! the singer's take starts when it is played back, so a note begun a little
// early is heard whole
export const takeLeadMs = 150;

export interface RhythmExerciseProps {
  audio?: LearnAudioSession;
  random?: RandomSource;
  initialScreen?: RhythmEchoScreen;
  initialLevel?: LearnLevel;
  // Sing Rhythm only. Injected by stories and tests, which never touch navigator.permissions.
  queryMicPermission?: () => Promise<MicPermissionState>;
  initialFailure?: MicFailure; // with initialScreen 'failed'
}

export const RhythmEchoPage = (props: RhythmExerciseProps) => (
  <RhythmExercisePage variant={rhythmEchoVariant} {...props} />
);

export const RhythmExercisePage = ({ variant, ...props }: RhythmExerciseProps & { variant: RhythmExerciseVariant }) => {
  const { module, input } = variant;
  const voice = input === 'voice';
  const audio = props.audio ?? getLearnAudioSession();
  const random = props.random ?? Math.random;
  const queryPermission = props.queryMicPermission ?? queryMicPermission;
  const { progress, updateProgress, flush } = useLearnProgress();
  const today = useCurrentDate();
  const [level, setLevel] = useState(props.initialLevel ?? LearnLevel.Easy);
  const [screen, setScreen] = useState<RhythmEchoScreen>(props.initialScreen ?? 'start');
  const [afterCalibration, setAfterCalibration] = useState<RhythmEntry>('start');
  const [justCalibrated, setJustCalibrated] = useState(false);
  const [failure, setFailure] = useState<MicFailure | undefined>(props.initialFailure);
  const mountedRef = useRef(true);
  const micGrantedRef = useRef(false); // the browser says the microphone is already allowed
  const explainedRef = useRef(false); // the singer has been through the explanation screen
  const entryRef = useRef<RhythmEntry>('session'); // what the explanation or a failure's Try again leads on to
  const { unlocked } = moduleProgress(progress, module);
  // "Back to Rhythm" and the Rhythm class screen it leads to (issue #3719); resolved on use, not at
  // import: a slip in learn_classes.ts must not take the whole app down
  const back = () => learnModuleBack(module);

  // Leaving the page silences whatever is still scheduled, ends the microphone if one is open and
  // rests the session (the context is never closed): leave() is the session's whole teardown.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      audio.leave();
    };
  }, [audio]);

  // A sung exercise has the session report sung onsets and keep its rolling capture (for "Hear both
  // again") for as long as the page is mounted, and no longer: the next exercise wants neither
  useEffect(() => {
    if (!voice) {
      return;
    }
    audio.setOnsetsEnabled(true);
    audio.setCaptureEnabled(true);
    return () => {
      audio.setOnsetsEnabled(false);
      audio.setCaptureEnabled(false);
    };
  }, [audio, voice]);

  // Whether the microphone still has to be explained: not when the browser has already granted it
  useEffect(() => {
    if (!voice) {
      return;
    }
    void queryPermission().then((permission) => {
      micGrantedRef.current = permission === 'granted';
    });
  }, [queryPermission, voice]);

  // Out of the exercise, by the header's arrow or the summary's button: back to the Rhythm class
  // screen (issue #3719), stepping back to it when it is the entry before (learn_navigation.tsx)
  const onBack = useLearnExit(back().path);

  // Starts audio from the tap that called it, which for a sung exercise means opening the
  // microphone; false when it would not open, with the failure screen already showing
  const resume = useCallback(async (): Promise<boolean> => {
    if (!voice) {
      await audio.start();
      return true;
    }
    try {
      await audio.openMic();
      return true;
    } catch (error) {
      if (mountedRef.current) {
        setFailure(micFailureOf(error));
        setScreen('failed');
      }
      return false;
    }
  }, [audio, voice]);

  // The way in behind Start, the Calibrate link, the explanation's Continue and a failure's Try
  // again - each a tap, which is the user gesture that starts audio. The first session on a device
  // calibrates first.
  const enter = useCallback(
    async (entry: RhythmEntry) => {
      entryRef.current = entry;
      if (voice && !micGrantedRef.current && !explainedRef.current) {
        setScreen('permission'); // its Continue comes back here, explained
        return;
      }
      if (!(await resume()) || !mountedRef.current) {
        return;
      }
      if (entry === 'start' || audio.getCalibrationOffsetMs(input) === undefined) {
        setAfterCalibration(entry);
        setScreen('calibration');
        return;
      }
      setScreen('session');
    },
    [audio, input, resume, voice],
  );

  const onExplained = useCallback(() => {
    explainedRef.current = true;
    void enter(entryRef.current);
  }, [enter]);

  const onCalibrated = useCallback(() => {
    setJustCalibrated(true);
    setScreen(afterCalibration);
    if (afterCalibration === 'start') {
      audio.endMic(); // nothing open for a tapped exercise
      void audio.stop(); // back to the start screen: nothing to play until the next Start
    }
  }, [afterCalibration, audio]);

  const title = `${learnModuleName(module)} · ${learnLevelName(level)}`;

  return (
    <OuterPageContent id={variant.pageId}>
      <Header title={title} onBack={onBack} />
      {screen === 'start' && (
        <RhythmStartScreen
          input={input}
          level={level}
          unlocked={unlocked}
          selectable={selectableLevels}
          calibrated={audio.getCalibrationOffsetMs(input) !== undefined}
          justCalibrated={justCalibrated}
          onSelectLevel={setLevel}
          onStart={() => void enter('session')}
          onCalibrate={() => void enter('start')}
        />
      )}
      {screen === 'permission' && <RangePermissionScreen onContinue={onExplained} />}
      {screen === 'failed' && (
        <SingingFailureScreen
          failure={failure ?? 'unknown'}
          idPrefix={variant.idPrefix}
          backLabel={back().label}
          onRetry={() => void enter(entryRef.current)}
          onBack={() => void onBack()}
        />
      )}
      {screen === 'calibration' && <RhythmCalibration audio={audio} input={input} onCalibrated={onCalibrated} />}
      {screen === 'session' && (
        <RhythmEchoSession
          audio={audio}
          random={random}
          variant={variant}
          level={level}
          progress={progress}
          today={today}
          updateProgress={updateProgress}
          flush={flush}
          resume={resume}
          onBack={() => void onBack()}
        />
      )}
    </OuterPageContent>
  );
};

// The "Hearing you" meter of a sung exercise, standing where the tap button would be: the level of
// each frame the microphone delivers. It keeps the level to itself, so a frame re-renders the meter
// and not the screen around it.
const RhythmVoiceMeter = ({ audio }: { audio: LearnAudioSession }) => {
  const [level, setLevel] = useState(0);
  useEffect(() => audio.onFrame((frame) => setLevel(levelForDbfs(frame.dbfs))), [audio]);
  return <HearingYouMeter level={level} />;
};

function voiceMeterFor(input: RhythmInput, audio: LearnAudioSession): ReactNode {
  return input === 'voice' ? <RhythmVoiceMeter audio={audio} /> : undefined;
}

// ---- Warm-up ---------------------------------------------------------------------------------

// Owns one warm-up run (issue #3523): schedules its clicks a bar at a time, ticks it from the audio
// clock, collects raw (unaligned) tap stamps and, the moment the taps lock in, silences the clicks
// still to come and stores the offset. The screen then shows Next, which hands back to the page;
// a run that reaches the end of its clicks without locking offers Try again, which starts a fresh
// one from its listen bar.
//
// A sung warm-up (Sing Rhythm) takes its taps from the microphone: each sung note's time, less the
// reported output delay (see the header), and what it stores is the voice offset.
const RhythmCalibration = ({
  audio,
  input,
  onCalibrated,
}: {
  audio: LearnAudioSession;
  input: RhythmInput;
  onCalibrated: () => void;
}) => {
  const voice = input === 'voice';
  const [run, setRun] = useState<CalibrationRun>(() => startCalibrationRun(audio.nowMs()));
  // Taken off every sung note before it is matched with a click, and added back to what is stored
  const [priorMs] = useState(() => (voice ? (audio.outputDelayMs() ?? 0) : 0));
  const scheduledRef = useRef<{ timeline: WarmUpTimeline; throughBar: number } | undefined>(undefined);
  const storedRef = useRef<WarmUpTimeline | undefined>(undefined);
  const running = isCalibrating(run);
  const phase = calibrationPhase(run);
  const outcome = calibrationOutcome(run);
  const tapOpen = isCalibrationTapOpen(run);
  const bar = calibrationBar(run);

  // The clicks reach the audio graph a bar at a time: the listen bar and the first tapping bar when
  // the run starts, then the next bar whenever the clock enters a new one, each bar once. So the
  // clicks stop within a bar of the ticking stopping - a lock, a Try again, a tab the browser
  // throttled - instead of all twelve bars sounding whatever the run does, and the lock has at most
  // one bar of them to silence.
  useEffect(() => {
    const scheduled = scheduledRef.current;
    if (scheduled?.timeline !== run.timeline) {
      // a fresh run; StrictMode runs effects twice, and the ref keeps each bar to one scheduling
      scheduledRef.current = { timeline: run.timeline, throughBar: 1 };
      audio.scheduleCalibration([
        ...calibrationBarClicksMs(run.timeline, 0),
        ...calibrationBarClicksMs(run.timeline, 1),
      ]);
      return;
    }
    const nextBar = (bar ?? -1) + 1;
    if (!running || nextBar <= scheduled.throughBar) {
      return; // the run is over, or the bar after this one is already scheduled
    }
    const clicksMs = calibrationBarClicksMs(run.timeline, nextBar);
    if (clicksMs.length === 0) {
      return; // the last bar is sounding; there is nothing after it
    }
    scheduled.throughBar = nextBar;
    audio.scheduleCalibration(clicksMs);
  }, [audio, bar, running, run.timeline]);

  useAudioClock(audio, running, (nowMs) => setRun((current) => calibrationTick(current, nowMs)));

  // Judged on the tap's own time, exactly as the run judges it, so a tap is never sounded and then
  // dropped; the handler depends on the two things that rule reads rather than on the run, which is
  // a new object on every tick
  const onTap = useCallback(
    (perfMs: number) => {
      const tapMs = audio.stampTapMs(perfMs); // raw: the offset is what this run measures
      if (!isWarmUpTapOpen(run.timeline, outcome, tapMs)) {
        return; // the listen bar, or the run has ended: the tap is neither sounded nor kept
      }
      giveTapFeedback(audio);
      setRun((current) => calibrationTap(current, tapMs));
    },
    [audio, outcome, run.timeline],
  );
  // Listening from the start of the run, the listen bar included: a space press there is swallowed
  // (no page scroll) and one in the gap between two frames as tapping opens is not lost
  useSpaceBarTap(running && !voice, onTap);

  // A sung warm-up's taps: raw, like a tap's stamp, and gated by the run itself (calibrationTap)
  useEffect(() => {
    if (!voice) {
      return;
    }
    return audio.onSungOnset((onset) => setRun((current) => calibrationTap(current, onset.timeMs - priorMs)));
  }, [audio, priorMs, voice]);

  // On the lock, once per run: the clicks after the locking one never sound, and the offset the
  // rest of the exercise aligns taps with is stored
  useEffect(() => {
    if (outcome?.kind !== 'locked' || storedRef.current === run.timeline) {
      return;
    }
    storedRef.current = run.timeline;
    audio.cancelScheduled();
    audio.setCalibrationOffsetMs(outcome.offsetMs + priorMs, input);
  }, [audio, input, outcome, priorMs, run.timeline]);

  useEnterKey(phase === 'locked', onCalibrated);

  const onRetry = useCallback(() => {
    audio.cancelScheduled();
    setRun(startCalibrationRun(audio.nowMs()));
  }, [audio]);

  return (
    <RhythmCalibrationScreen
      phase={phase}
      lights={calibrationLights(run)}
      cue={calibrationCue(run)}
      streak={calibrationStreak(run)}
      tapOpen={tapOpen}
      onTap={onTap}
      onNext={onCalibrated}
      onRetry={onRetry}
      voiceMeter={voiceMeterFor(input, audio)}
    />
  );
};

// ---- Session ---------------------------------------------------------------------------------

function isPatternPhase(state: RhythmEchoState): state is RhythmPatternState {
  return 'tapsMs' in state;
}

// Ticks flow while a pattern or a replay is running; the result and summary screens are still
function isClockDriven(state: RhythmEchoState): boolean {
  return isPatternPhase(state) || state.phase === 'replay';
}

// The singer's take of one pattern (Sing Rhythm): the sung bar, cut from the rolling capture when
// the pattern was scored, for "Hear both again". `result` says which attempt it belongs to.
interface RhythmTake {
  result: PatternResult;
  slice: CaptureSlice | undefined; // undefined when the capture held none of the bar
}

const RhythmEchoSession = ({
  audio,
  random,
  variant,
  level,
  progress,
  today,
  updateProgress,
  flush,
  resume,
  onBack,
}: {
  audio: LearnAudioSession;
  random: RandomSource;
  variant: RhythmExerciseVariant;
  level: LearnLevel;
  progress: LearnProgress;
  today: DateString;
  updateProgress: (update: LearnProgressUpdate) => void;
  flush: () => Promise<void>;
  resume: () => Promise<boolean>; // starts audio again from a tap; false when the microphone would not open
  onBack: () => void;
}) => {
  const { module, input } = variant;
  const voice = input === 'voice';
  // The voice offset as it stood when the session opened: it cannot change under a session, since
  // the warm-up is a screen of its own
  const [voiceOffsetMs] = useState(() => (voice ? (audio.getCalibrationOffsetMs('voice') ?? 0) : 0));
  const [state, dispatch] = useReducer(reduceRhythmEcho, { level, random }, (init) =>
    initialRhythmEchoState(
      init.level,
      generateSession(init.level, init.random),
      voice ? { input, settleMs: voiceOffsetMs + voiceSettleMs } : {},
    ),
  );
  const scheduledTimelineRef = useRef<RhythmTimeline | undefined>(undefined);
  const scheduledReplayRef = useRef<RhythmReplay | undefined>(undefined);
  const takeRef = useRef<RhythmTake | undefined>(undefined);
  const [recorded, setRecorded] = useState<RecordedSession | undefined>(undefined);

  // The first pattern begins as soon as the session mounts (Start already resumed audio)
  useEffect(() => {
    dispatch({ type: 'start', startMs: audio.nowMs() }); // ignored unless idle
  }, [audio]);

  // A sung pattern's take is cut out of the rolling capture as its result arrives - the reducer has
  // already waited for the bar's last audio (voiceSettleMs) - and replaces the take before it. The
  // microphone heard the bar the voice offset later than the singer sang it, so that is where the
  // bar sits in the capture.
  const result = state.phase === 'result' ? state.result : undefined;
  const resultTimeline = state.phase === 'result' ? state.timeline : undefined;
  useEffect(() => {
    if (!voice || !result || !resultTimeline || takeRef.current?.result === result) {
      return;
    }
    const capture = audio.takeCapture();
    const startMs = resultTimeline.tapBarStartMs - takeLeadMs + voiceOffsetMs;
    const endMs = resultTimeline.tapsEndMs + voiceOffsetMs;
    takeRef.current = { result, slice: capture && sliceCapture(capture, startMs, endMs) };
  }, [audio, result, resultTimeline, voice, voiceOffsetMs]);

  // Sounds follow the reducer: each new pattern timeline and each new replay is scheduled once. A
  // replay is the pattern's percussion and then, a bar later, what the singer gave back: percussion
  // on each tap, or the singer's own take where one was kept (a sung session whose capture held
  // the bar; without it the notes they sang sound as percussion, like taps).
  useEffect(() => {
    if (isPatternPhase(state) && state.timeline !== scheduledTimelineRef.current) {
      scheduledTimelineRef.current = state.timeline;
      audio.schedulePattern(state.timeline);
    }
    if (state.phase === 'replay' && state.replay !== scheduledReplayRef.current) {
      scheduledReplayRef.current = state.replay;
      const { replay, timeline } = state;
      const take = takeRef.current;
      const slice = take?.result === state.result ? take.slice : undefined;
      if (!slice) {
        audio.scheduleTaps([...replay.patternOnsetsMs, ...replay.tapOnsetsMs]);
        return;
      }
      audio.scheduleTaps(replay.patternOnsetsMs);
      // The take's first sample was sung this long after Go! (before it, by the lead), and Go! is
      // one bar into the replay
      const sungAtMs = slice.startMs - voiceOffsetMs - timeline.tapBarStartMs;
      const atMs = replay.startMs + rhythmBeatsPerBar * timeline.beatMs + sungAtMs;
      void audio.playRecording(slice.samples, slice.sampleRate, atMs);
    }
  }, [audio, state, voiceOffsetMs]);

  // A sung session's taps: the start of each sung note, at the time the singer made it (the
  // microphone's time less the voice offset). The reducer keeps the ones inside the pattern's
  // window and drops the rest, so nothing is gated here.
  useEffect(() => {
    if (!voice) {
      return;
    }
    return audio.onSungOnset((onset) => dispatch({ type: 'tap', tapMs: alignTap(onset.timeMs, voiceOffsetMs) }));
  }, [audio, voice, voiceOffsetMs]);

  useAudioClock(audio, isClockDriven(state), (nowMs) => dispatch({ type: 'tick', nowMs }));

  const onTap = useCallback(
    (perfMs: number) => {
      // The gate is isTapOpen, from Set: a keen tap a beat before Go! is kept and sounded while the
      // button still looks closed (its look follows isTapButtonOpen, from Go!). Judged on the
      // aligned time the reducer keeps, not the raw clock, so the gate and the reducer agree: a tap
      // within the device offset after Set would otherwise sound here and be dropped there.
      const tapMs = audio.alignedTapMs(perfMs);
      if (!isTapOpen(state, tapMs)) {
        return; // a tap before tapping opened, or after it closed
      }
      giveTapFeedback(audio);
      dispatch({ type: 'tap', tapMs });
    },
    [audio, state],
  );
  useSpaceBarTap(isPatternPhase(state) && !voice, onTap);

  const onStop = useCallback(() => {
    audio.cancelScheduled();
    dispatch({ type: 'stop' });
  }, [audio]);

  // After the eighth pattern the session is recorded here, on the tap that opens the summary, so
  // the summary's first render already has the best-score and unlock flags
  const finishSession = useCallback(() => {
    const summary = summarizeSession(state.results);
    const now = nowOn(today);
    const session = { module, level, score: summary.score, now };
    setRecorded(recordLearnSession({ progress, ...session }));
    updateProgress((current) => recordLearnSession({ progress: current, ...session }).progress);
    void flush();
    audio.cancelScheduled();
    audio.endMic(); // a sung session's microphone; nothing open for a tapped one
    void audio.stop();
  }, [audio, flush, level, module, progress, state.results, today, updateProgress]);

  const onNext = useCallback(() => {
    if (state.phase === 'replay') {
      audio.cancelScheduled();
    }
    if (state.patternIndex + 1 >= state.patterns.length) {
      finishSession();
    }
    dispatch({ type: 'next', startMs: audio.nowMs() });
  }, [audio, finishSession, state.patternIndex, state.patterns.length, state.phase]);

  useEnterKey(state.phase === 'result' || state.phase === 'replay', onNext);

  // The same pattern again; the reducer ignores a second retry, and nothing is recorded here
  const onRetry = useCallback(() => {
    if (state.phase === 'replay') {
      audio.cancelScheduled();
    }
    dispatch({ type: 'retry', startMs: audio.nowMs() });
  }, [audio, state.phase]);

  const onReplay = useCallback(() => {
    if (state.phase === 'replay') {
      audio.cancelScheduled();
    }
    dispatch({ type: 'replay', startMs: audio.nowMs() + replayLeadMs });
  }, [audio, state.phase]);

  const onPlayAgain = useCallback(async () => {
    if (!(await resume())) {
      return; // the microphone would not open again: the page is showing why
    }
    setRecorded(undefined);
    dispatch({ type: 'restart', patterns: generateSession(level, random), startMs: audio.nowMs() });
  }, [audio, level, random, resume]);

  if (isPatternPhase(state)) {
    // What the screen lights (the syllable sounding, Ready · Set · Go!, the button opening on Go!)
    // follows the sound as it leaves the speaker, a quarter of a second after the clock on the
    // Samsungs (issue #3710). Only the picture: phases, the tap gate and scoring keep the clock.
    const shown = { ...state, nowMs: state.nowMs - audio.heardLagMs() };
    return <RhythmPatternScreen state={shown} onTap={onTap} onStop={onStop} voiceMeter={voiceMeterFor(input, audio)} />;
  }
  if (state.phase === 'result' || state.phase === 'replay') {
    return <RhythmResultScreen state={state} onNext={onNext} onReplay={onReplay} onRetry={onRetry} />;
  }
  if (state.phase === 'summary') {
    const after = recorded?.progress ?? progress;
    return (
      <RhythmSummaryScreen
        learnModule={module}
        level={level}
        summary={state.summary}
        module={moduleProgress(after, module)}
        dayStreak={currentStreak(after, today)}
        isNewBest={recorded?.isNewBest ?? false}
        unlockedLevel={recorded?.unlockedLevel}
        onPlayAgain={() => void onPlayAgain()}
        backLabel={learnModuleBack(module).label}
        onBack={onBack}
      />
    );
  }
  return null; // idle, for the one render before the mount effect starts the first pattern
};
